import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Phone, Clock, ArrowLeft, ChevronRight, Calendar, User } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { blogPosts as legacyBlogPosts } from '../blogData';
import { additionalBlogPosts } from '../newContent';
import { companyInfo } from '../mock';
import SEO from './SEO';
import { ArticleContent } from './ArticleContent';
import { blogPostingSchema, breadcrumbSchema } from '../seo/schemas';

const blogPosts = [...additionalBlogPosts, ...legacyBlogPosts];

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <SEO title="Article Not Found" description="The blog article you are looking for does not exist." path={`/blog/${slug || ''}`} noindex />
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <Link to="/blog">
            <Button className="bg-yellow-400 text-black font-bold hover:bg-yellow-500">Browse Articles</Button>
          </Link>
        </div>
      </div>
    );
  }

  const relatedPosts = blogPosts
    .filter(p => p.id !== post.id && p.category === post.category)
    .slice(0, 2);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Trim excerpt to a clean meta description
  const metaDesc = post.excerpt && post.excerpt.length > 50
    ? post.excerpt
    : `${post.title}. Expert electrical advice from experienced Brooklyn electricians.`;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={post.title}
        description={metaDesc}
        path={`/blog/${post.slug}`}
        image={post.image}
        keywords={`${post.title.toLowerCase()}, ${post.category.toLowerCase()}, brooklyn electrician`}
        jsonLd={[
          blogPostingSchema({
            title: post.title,
            description: metaDesc,
            slug: post.slug,
            image: post.image,
            author: post.author,
            date: post.date,
          }),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
        ]}
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2" data-testid="post-home-link">
              <img
                src="https://customer-assets.emergentagent.com/job_jetco-electrical/artifacts/x1j92naa_Untitled%20design.png"
                alt="Jetco Electrical Contractors logo"
                className="h-12 w-auto"
              />
            </Link>
            <a href={`tel:${companyInfo.phone}`} data-testid="post-call-now-link">
              <Button className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold shadow-sm">
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Article Header */}
      <article className="pt-24 pb-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <button onClick={() => navigate('/blog')} className="flex items-center text-yellow-700 hover:text-yellow-900 mb-6 transition-colors" data-testid="post-back-button">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </button>

            {/* Breadcrumb (visible to humans + screen readers) */}
            <nav aria-label="Breadcrumb" className="mb-4 text-sm text-gray-500">
              <Link to="/" className="hover:text-yellow-700">Home</Link>
              <span className="mx-2">/</span>
              <Link to="/blog" className="hover:text-yellow-700">Blog</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-700">{post.category}</span>
            </nav>

            <span className="inline-block text-sm font-semibold text-yellow-700 bg-yellow-100 border border-yellow-300 px-4 py-2 rounded-full mb-4">
              {post.category}
            </span>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-8">
              <div className="flex items-center">
                <User className="w-5 h-5 mr-2 text-yellow-600" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-yellow-600" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-5 h-5 mr-2 text-yellow-600" />
                <span>{post.readTime}</span>
              </div>
            </div>

            {/* Featured Image */}
            <div className="aspect-video rounded-2xl overflow-hidden mb-12 shadow-md border border-gray-200">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <ArticleContent content={post.content} />
            </div>

            {/* CTA Box */}
            <div className="mt-12 bg-yellow-400 rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold text-black mb-4">
                Need Professional Help?
              </h3>
              <p className="text-gray-900 mb-6">
                Our expert electricians are ready to assist with your electrical needs
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-black text-white hover:bg-gray-900"
                  onClick={scrollToContact}
                  data-testid="post-cta-estimate"
                >
                  Get Free Estimate
                </Button>
                <a href={`tel:${companyInfo.phone}`} data-testid="post-cta-phone">
                  <Button size="lg" variant="outline" className="border-2 border-black text-black hover:bg-black hover:text-white bg-transparent">
                    <Phone className="w-5 h-5 mr-2" />
                    {companyInfo.phone}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedPosts.length > 0 && (
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Related <span className="text-yellow-500">Articles</span></h2>
            <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
              {relatedPosts.map(relPost => (
                <Card key={relPost.id} className="hover:shadow-lg transition-all border border-gray-200 hover:border-yellow-400 bg-white" data-testid={`related-post-${relPost.slug}`}>
                  <div className="aspect-video overflow-hidden rounded-t-lg">
                    <img
                      src={relPost.image}
                      alt={relPost.title}
                      loading="lazy"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-yellow-700 bg-yellow-100 border border-yellow-300 px-3 py-1 rounded-full">
                        {relPost.category}
                      </span>
                      <span className="text-sm text-gray-500 flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {relPost.readTime}
                      </span>
                    </div>
                    <CardTitle className="text-xl text-gray-900">{relPost.title}</CardTitle>
                    <CardDescription className="text-gray-600">{relPost.excerpt}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link to={`/blog/${relPost.slug}`}>
                      <Button className="w-full bg-yellow-400 text-black font-bold hover:bg-yellow-500">
                        Read Article
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-300">© 2025 {companyInfo.name}. All rights reserved.</p>
          <div className="mt-4">
            <a href={`tel:${companyInfo.phone}`} className="text-yellow-400 hover:text-yellow-300 transition-colors">{companyInfo.phone}</a>
            <span className="mx-3 text-gray-600">|</span>
            <a href={`mailto:${companyInfo.email}`} className="text-yellow-400 hover:text-yellow-300 transition-colors">{companyInfo.email}</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;
